// src/pages/TeamAssessment.js
// Shows combined team assessment scores for team leaders

import { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs, doc, getDoc
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, withCurrentLabels } from '../utils/surveyQuestions';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from 'recharts';
import PageLoading from '../components/PageLoading';

function scoreColor(score) {
  if (score >= 4) return '#2d6a4f';
  if (score >= 3) return '#f4a261';
  return '#e63946';
}

function scoreLabel(score) {
  if (score >= 4.5) return { label: 'Excellent', cls: 'badge-green' };
  if (score >= 3.5) return { label: 'Good', cls: 'badge-blue' };
  if (score >= 2.5) return { label: 'Needs attention', cls: 'badge-yellow' };
  return { label: 'Critical', cls: 'badge-red' };
}

function computeTeamScores(responses, categories) {
  const catScores = {};
  const categoryDetails = {};

  categories.forEach(cat => {
    const allScores = [];
    const memberScores = {};

    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) {
          allScores.push(r.answers[q.id]);
          if (!memberScores[r.userId]) memberScores[r.userId] = [];
          memberScores[r.userId].push(r.answers[q.id]);
        }
      });
    });

    const avgScore = allScores.length
      ? +(allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(2)
      : 0;

    catScores[cat.id] = avgScore;
    categoryDetails[cat.id] = {
      average: avgScore,
      min: allScores.length ? Math.min(...allScores) : 0,
      max: allScores.length ? Math.max(...allScores) : 0,
      count: allScores.length,
    };
  });

  return { catScores, categoryDetails };
}

export default function TeamAssessment({ teamId: externalTeamId } = {}) {
  const { currentUser, userProfile } = useAuth();
  const [team, setTeam] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamScores, setTeamScores] = useState({});
  const [categoryDetails, setCategoryDetails] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);

  const teamId = externalTeamId || userProfile?.teamId;
  const role = userProfile?.role || 'free';
  const canDownloadPdf = ['team_leader', 'consultant', 'admin'].includes(role);

  useEffect(() => {
    if (!pdfDropdownOpen) return;
    const close = () => setPdfDropdownOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pdfDropdownOpen]);

  async function handleDownloadPdf(format = 'landscape') {
    setPdfDropdownOpen(false);
    setGeneratingPdf(true);
    setPdfError(null);
    try {
      const { getAuth } = await import('firebase/auth');
      const idToken = await getAuth().currentUser.getIdToken();
      const fnUrl = 'https://us-central1-meetingtraining.cloudfunctions.net/generateTeamAssessmentPDF';
      const response = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, teamId, format }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'PDF generation failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${team?.name || 'team'}-team-assessment-${format}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err.message);
      console.error('Download PDF error:', err);
    } finally {
      setGeneratingPdf(false);
    }
  }

  useEffect(() => {
    if (teamId) loadData();
    else setLoading(false);
  }, [teamId]);

  async function loadData() {
    try {
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      if (teamSnap.exists()) {
        setTeam({ id: teamSnap.id, ...teamSnap.data() });
        if (teamSnap.data().customQuestions) {
          setCategories(withCurrentLabels(teamSnap.data().customQuestions));
        }
      }

      const responsesSnap = await getDocs(
        query(collection(db, 'responses'), where('teamId', '==', teamId))
      );

      const allResponses = responsesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setResponses(allResponses);

      const { catScores, categoryDetails: catDetails } = computeTeamScores(
        allResponses,
        categories
      );
      setTeamScores(catScores);
      setCategoryDetails(catDetails);
    } catch (err) {
      console.error('Error loading team assessment:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <PageLoading text="Loading team assessment..." />;
  }

  if (!team || role === 'free') {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>Team Assessment</h2>
          <p>Only team leaders can view team assessments.</p>
        </div>
      </div>
    );
  }

  if (responses.length === 0) {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>{team?.name || 'Team'} — Assessment Overview</h2>
          <p>Combined assessment results from all team members</p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>📋</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>No assessments submitted yet</div>
          <div style={{ fontSize: '0.9rem', color: 'var(--ink-muted)' }}>Results will appear here once team members complete their assessments.</div>
        </div>
      </div>
    );
  }

  const radarData = categories.map(cat => ({
    name: cat.label,
    score: teamScores[cat.id] || 0,
    fullMark: 5,
  }));

  const nonZeroScores = Object.values(teamScores).filter(s => s > 0);
  const overallScore = nonZeroScores.length > 0
    ? +(nonZeroScores.reduce((a, b) => a + b, 0) / nonZeroScores.length).toFixed(2)
    : 0;

  const barData = categories.map(cat => ({
    category: cat.label,
    average: teamScores[cat.id] || 0,
    min: categoryDetails[cat.id]?.min || 0,
    max: categoryDetails[cat.id]?.max || 0,
  }));

  return (
    <div className="fade-up">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>{team?.name || 'Team'} — Assessment Overview</h2>
          <p>Combined assessment results from all {responses.length} team members</p>
        </div>
        {canDownloadPdf && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', borderRadius: 6, overflow: 'visible' }}>
                <button
                  onClick={() => handleDownloadPdf('landscape')}
                  disabled={generatingPdf}
                  style={{
                    padding: '8px 14px',
                    background: generatingPdf ? '#9ca3af' : '#1e2e45',
                    color: 'white', border: 'none', borderRadius: '6px 0 0 6px',
                    fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 12,
                    cursor: generatingPdf ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.3px', whiteSpace: 'nowrap',
                  }}
                >
                  {generatingPdf ? 'Generating…' : '↓ Download PDF'}
                </button>
                <button
                  onClick={e => { e.stopPropagation(); if (!generatingPdf) setPdfDropdownOpen(o => !o); }}
                  disabled={generatingPdf}
                  style={{
                    padding: '8px 10px',
                    background: generatingPdf ? '#9ca3af' : '#2a3d5b',
                    color: 'white', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '0 6px 6px 0',
                    cursor: generatingPdf ? 'not-allowed' : 'pointer', fontSize: 10,
                  }}
                >
                  ▾
                </button>
              </div>
              {pdfDropdownOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                  background: 'white', border: '1px solid var(--border)',
                  borderRadius: 8, zIndex: 200, minWidth: 190,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', overflow: 'hidden',
                }}>
                  {[
                    { format: 'landscape', label: 'Slide Deck', sub: 'Landscape · 1280×720' },
                    { format: 'mobile',    label: 'Mobile',     sub: 'Portrait · 430×932' },
                  ].map(({ format, label, sub }) => (
                    <button
                      key={format}
                      onClick={() => handleDownloadPdf(format)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 14px', border: 'none', background: 'transparent',
                        cursor: 'pointer', borderBottom: '1px solid var(--border)',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{label}</div>
                      <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 1 }}>{sub}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {pdfError && (
              <div style={{ fontSize: 11, color: 'var(--danger)' }}>{pdfError}</div>
            )}
          </div>
        )}
      </div>

      {/* Overall Score Card */}
      <div className="team-score-hero" style={{
        marginBottom: 32,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
        borderRadius: 'var(--radius-lg)',
        padding: '36px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 24,
      }}>
        <div>
          <div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>
            Team Health Score
          </div>
          <div style={{
            fontSize: '4.5rem', fontWeight: 700, color: 'white',
            fontFamily: 'var(--font-display)', lineHeight: 1, marginBottom: 12,
          }}>
            {overallScore.toFixed(1)}
            <span style={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.3)' }}>/5</span>
          </div>
          <span className={`badge ${scoreLabel(overallScore).cls}`} style={{ fontSize: '0.85rem', padding: '6px 16px' }}>
            {scoreLabel(overallScore).label}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>
            {responses.length}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            responses
          </div>
        </div>
      </div>

      {/* Bar Chart — hidden per design decision */}

      {/* Category Breakdown Cards */}
      <div className="card" style={{ marginBottom: 32 }}>
        <div className="card-title" style={{ marginBottom: 20 }}>Breakdown</div>
        <div className={`team-assessment-cat-grid ${categories.length === 4 ? 'team-assessment-cat-grid-2' : ''}`}
          style={{ display: 'grid', gap: 16 }}>
          {categories.map(cat => {
            const score = teamScores[cat.id] || 0;
            const details = categoryDetails[cat.id];
            const label = scoreLabel(score);
            return (
              <div key={cat.id} style={{ padding: 16, background: 'var(--bg-soft)', borderRadius: 8, border: `2px solid ${cat.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color }} />
                  <strong style={{ fontSize: '0.9rem' }}>{cat.label}</strong>
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: scoreColor(score), marginBottom: 4 }}>
                  {score.toFixed(1)}<span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>/5</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', fontWeight: 600, marginBottom: 12 }}>{label.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', lineHeight: 1.6 }}>
                  <div>Range: {details?.min || 0} – {details?.max || 0}</div>
                  <div>Based on {details?.count || 0} responses</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Breakdown — Top 5 / Bottom 5 behaviors + full list */}
      {(() => {
        // Build flat list of all behaviors with avg scores
        const allBehaviors = [];
        categories.forEach(cat => {
          cat.questions.forEach(q => {
            const scores = responses
              .map(r => r.answers?.[q.id])
              .filter(v => v != null);
            const avg = scores.length
              ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
              : null;
            allBehaviors.push({
              id: q.id,
              text: q.text,
              category: cat.label,
              color: cat.color,
              avg,
              count: scores.length,
            });
          });
        });

        const scored = allBehaviors.filter(b => b.avg !== null);
        const sorted = [...scored].sort((a, b) => b.avg - a.avg);
        const top5 = sorted.slice(0, 5);
        const bottom5 = [...sorted].reverse().slice(0, 5);

        const BehaviorRow = ({ b, rank, highlight }) => (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: highlight === 'top' ? 'rgba(45,106,79,0.07)' : highlight === 'bottom' ? 'rgba(230,57,70,0.06)' : 'var(--bg-soft)',
            marginBottom: 6,
          }}>
            {rank !== undefined && (
              <div style={{ width: 24, textAlign: 'center', fontSize: '0.72rem', color: 'var(--ink-muted)', fontWeight: 700, flexShrink: 0 }}>
                {rank}
              </div>
            )}
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: '0.84rem', color: 'var(--ink)', lineHeight: 1.4 }}>{b.text}</div>
            <div style={{ fontSize: '0.65rem', color: 'var(--ink-muted)', marginRight: 8, flexShrink: 0, textAlign: 'right' }}>{b.category}</div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.1rem', fontWeight: 700,
              color: highlight === 'top' ? '#2d6a4f' : highlight === 'bottom' ? '#e63946' : scoreColor(b.avg),
              minWidth: 36, textAlign: 'right', flexShrink: 0,
            }}>
              {b.avg.toFixed(1)}
            </div>
          </div>
        );

        return (
          <div className="card" style={{ marginBottom: 32 }}>
            <div className="card-title" style={{ marginBottom: 24 }}>Detailed Breakdown</div>

            {/* Top 5 */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#2d6a4f', fontWeight: 700, marginBottom: 12 }}>Top 5 Behaviors</div>
              {top5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="top" />)}
            </div>

            {/* Bottom 5 */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#e63946', fontWeight: 700, marginBottom: 12 }}>Bottom 5 Behaviors</div>
              {bottom5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="bottom" />)}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: 'var(--border)', marginBottom: 24 }} />

            {/* Full list of 40 grouped by category */}
            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: 16 }}>All 40 Behaviors — Average Score</div>
              {categories.map(cat => {
                const catBehaviors = allBehaviors.filter(b => b.category === cat.label);
                return (
                  <div key={cat.id} style={{ marginBottom: 20 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      marginBottom: 8, paddingBottom: 6,
                      borderBottom: `2px solid ${cat.color}`,
                    }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }} />
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink)' }}>{cat.label}</span>
                    </div>
                    {catBehaviors.map(b => (
                      b.avg !== null
                        ? <BehaviorRow key={b.id} b={b} />
                        : (
                          <div key={b.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 14px', borderRadius: 8,
                            background: 'var(--bg-soft)', marginBottom: 6, opacity: 0.45,
                          }}>
                            <div style={{ flex: 1, fontSize: '0.84rem', color: 'var(--ink-muted)' }}>{b.text}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', minWidth: 36, textAlign: 'right' }}>—</div>
                          </div>
                        )
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Summary Stats */}
      {(() => {
        const memberCount = team?.memberEmails?.length || 0;
        const participationPct = memberCount > 0 ? Math.round((responses.length / memberCount) * 100) : null;
        const highCat = categories.length > 0
          ? categories.reduce((best, cat) => (teamScores[cat.id] || 0) > (teamScores[best.id] || 0) ? cat : best)
          : null;
        const lowCat = categories.length > 0
          ? categories.reduce((worst, cat) => (teamScores[cat.id] || 0) < (teamScores[worst.id] || 0) ? cat : worst)
          : null;

        return (
          <div className="card">
            <div className="card-title" style={{ marginBottom: 20 }}>Summary</div>
            <div className="team-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
              <div style={{ padding: 16, background: 'var(--bg-soft)', borderRadius: 8 }}>
                <div className="report-stat-label" style={{ marginBottom: 6 }}>Participation</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="report-stat-value">{responses.length}</div>
                  {memberCount > 0 && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>of {memberCount} members</div>
                  )}
                </div>
                {participationPct !== null && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>Response rate</span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: participationPct >= 80 ? 'var(--accent)' : participationPct >= 50 ? '#f4a261' : '#e63946' }}>{participationPct}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${participationPct}%`, background: participationPct >= 80 ? 'var(--accent)' : participationPct >= 50 ? '#f4a261' : '#e63946' }} />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: 16, background: 'var(--bg-soft)', borderRadius: 8 }}>
                <div className="report-stat-label" style={{ marginBottom: 12 }}>Category Highlights</div>
                {highCat && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: highCat.color, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', marginBottom: 1 }}>Strongest</div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--ink)' }}>{highCat.label}</div>
                      </div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 600, color: '#2d6a4f' }}>{(teamScores[highCat.id] || 0).toFixed(1)}</div>
                  </div>
                )}
                <div style={{ height: 1, background: 'var(--border)', marginBottom: 10 }} />
                {lowCat && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: lowCat.color, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', marginBottom: 1 }}>Needs attention</div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--ink)' }}>{lowCat.label}</div>
                      </div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 600, color: '#e63946' }}>{(teamScores[lowCat.id] || 0).toFixed(1)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
